<template>
  <div
    v-if="cmsPageContent"
    class="cms-slug"
    :class="{
      'container-EXPONDO': isInsideContainer,
      'lg:flex lg:py-10': shouldShowNavigation
    }"
  >
    <RenderContent
      v-if="shouldShowNavigation"
      class="lg:w-1/4"
      :content="filteredNavigationComponent"
    />
    <RenderContent
      :class="{ 'lg:ml-6 lg:w-3/4': shouldShowNavigation,
                'flex flex-col gap-4' : isHomePage
      }"
      :content="cmsPageContent"
      :is-components-overview-page="isComponentsOverviewPage"
    />
    <TrustedBy v-if="isHomePage" />
  </div>
</template>

<script lang='ts'>
import type { DynamicPageContent } from '~~/types/cms/DynamicPageContent';
import { CacheTagPrefix } from '~/composables/useCache';
import { buildLocalizedCmsPageList, getCmsPagePrefix } from '~~/helpers/cms/localizedCmsPage';
import { filterPublishedMenuItems } from '~~/helpers/cms/menuItems';
import { COMPONENTS } from '~~/constants/cms';
import type { ComponentData } from '~~/types/cms/CmsComponentData';

/*
* This page is prepared for CMS dynamic content rendering.
*/
export default defineComponent({
  name: 'DynamicCmsPage',
  props: {
    content: {
      type: Array,
      required: true,
    },
    currentPageSlug: {
      type: String,
      required: true,
    },
    isHomePage: {
      type: Boolean,
      required: true,
    },
  },
  setup (props: {
    content: DynamicPageContent
    currentPageSlug: string,
    isHomePage: boolean
  }) {

    const { content, currentPageSlug, isHomePage } = toRefs(props);
    const { callPageViewEvent } = usePageViewEvent();
    const { languageAndCountry } = useLocalization();
    const { publishedPages } = useDynamicPublishedCmsPages();
    const {
      isValidCategoryCMSPage,
      hasMenu,
      cmsPageContent,
      isInsideContainer,
      seoContent,
      isComponentsOverviewPage,
    } = useAbsoluteUrlPage(content);

    const localizedSlugs = computed(() => {

      if (isValidCategoryCMSPage.value) {

        return currentCategorySlugAllLocales.value;

      }

      const localizedSlugPagesList = buildLocalizedCmsPageList({
        languageAndCountry: languageAndCountry.value,
        pageSlug: props.currentPageSlug,
        publishedPages: publishedPages.value,
      });

      return localizedSlugPagesList || [];

    });

    const { addTags } = useCache();
    const { searchWithDefaultParameters, getters: { currentCategorySlugAllLocales } } = useCategories();

    const {
      loadComponent: loadNavigation,
      internalCmsContent: navigationComponent,
      canShowContent: hasNavigationContent,
    } = useInternalCms(COMPONENTS.BASIC_PAGE_NAVIGATION.SLUG);

    useLazyAsyncData(`category-slug-locales-${currentPageSlug.value}`, async () => {

      if (isValidCategoryCMSPage.value) {

        await searchWithDefaultParameters(currentPageSlug.value);

      }

      if (hasMenu.value && !hasNavigationContent.value) {

        await loadNavigation();

      }

      addTags([
        { prefix: CacheTagPrefix.View, value: 'cmspage' },
        { prefix: CacheTagPrefix.Block, value: currentPageSlug.value },
      ]);

      return '';

    });

    const isPageHreflangLinkHidden = computed(() => {

      // hide hreflangs if not home page, **except** if this CMS page has a prefix
      const hasCmsPrefix = getCmsPagePrefix({
        languageAndCountry: languageAndCountry.value,
        pageSlug: currentPageSlug.value,
        publishedPages: publishedPages.value,
      });

      if (hasCmsPrefix) {

        return false;

      }

      return !isHomePage.value;

    });

    const shouldShowNavigation = computed(() => {

      return hasMenu.value && hasNavigationContent.value;

    });

    const filteredNavigationComponent = computed(() =>
      navigationComponent.value.map((component: ComponentData) => ({
        ...component,
        menuItems: filterPublishedMenuItems({
          menuItems: component.menuItems,
          languageAndCountry: languageAndCountry.value,
          publishedPages: publishedPages.value,
        }),
      })),
    );

    // TODO nuxt 3 seo: add the definition of title and adjust meta tags to be visible:
    // https:// expondo.atlassian.net/browse/INSP-1154
    useMetaExtended({
      metaTitle: computed(() => seoContent.value?.title || ''),
      isPageIndexed: ref(true),
      slug: currentPageSlug,
      localisedSlugs: localizedSlugs,
      isPageHreflangLinkHidden,
    });
    onMounted(() => {

      callPageViewEvent();

    });

    return {
      cmsPageContent,
      shouldShowNavigation,
      filteredNavigationComponent,
      isInsideContainer,
      isComponentsOverviewPage,
    };

  },
});

</script>

<style lang="scss" scoped>
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
// TODO - harmonise vertical and horizontal spacings between Home and Category pages
// https://expondo.atlassian.net/browse/INSP-654
.cms-slug {
  .content-block-wrapper {
    :deep(.category-items),
    :deep(.marketing-card-container) {
      margin-left: auto;
      margin-right: auto;
      padding-right: 1.25rem;
      padding-left: 1.25rem;
      padding-top: 1rem;

      @include breakpoints.for-desktop {
        padding-right: 5rem;
        padding-left: 5rem;
      }
    }
  }
}
</style>
